import { defineStore } from 'pinia'
import { apiMutate } from '../composables/apiMutate'
import { expectArray } from '../composables/expectShape'
import { useAsyncResource, UnauthorizedError } from '../composables/useAsyncResource'

export interface AgentPrompt {
  id: number
  label: string
  text: string
  repo_slug: string | null
  position: number
}

interface PromptsResponse {
  prompts: AgentPrompt[]
}

export interface CreatePromptParams {
  label: string
  text: string
  repo_slug?: string | null
}

export interface UpdatePromptParams {
  label?: string
  text?: string
  repo_slug?: string | null
}

// Admin store for canned prompts — either global (`repo_slug`
// null) or scoped to one repo by slug. Managed from the Prompt Library
// admin page and consumed by the /agent composer's new-session picker.
export const useAgentPromptsStore = defineStore('agentPrompts', () => {
  const {
    data: prompts,
    loaded,
    loading,
    error: loadError,
    needsAuth,
    refresh: fetchAll,
  } = useAsyncResource<AgentPrompt[]>(
    async () => {
      const res = await fetch('/api/v1/agent/prompts', { credentials: 'include' })
      if (res.status === 401) throw new UnauthorizedError()
      if (!res.ok) throw new Error(`Failed to load prompts: ${res.status}`)
      const data = (await res.json()) as PromptsResponse
      return expectArray<AgentPrompt>(data.prompts, 'agent prompts')
    },
    { initialValue: [] },
  )

  async function create(params: CreatePromptParams): Promise<void> {
    await apiMutate('/api/v1/agent/prompts', {
      method: 'POST',
      body: { agent_prompt: params },
      failureLabel: 'Failed to add prompt',
    })
    await fetchAll()
  }

  async function update(id: number, params: UpdatePromptParams): Promise<void> {
    await apiMutate(`/api/v1/agent/prompts/${id}`, {
      method: 'PATCH',
      body: { agent_prompt: params },
      failureLabel: 'Failed to update prompt',
    })
    await fetchAll()
  }

  async function destroy(id: number): Promise<void> {
    await apiMutate(`/api/v1/agent/prompts/${id}`, {
      method: 'DELETE',
      failureLabel: 'Failed to remove prompt',
    })
    await fetchAll()
  }

  // Persists a new order for one scope at a time (ids must all share the
  // same repo_slug) — the caller is responsible for grouping by scope.
  async function reorder(ids: number[]): Promise<void> {
    await apiMutate('/api/v1/agent/prompts/reorder', {
      method: 'PATCH',
      body: { ids },
      failureLabel: 'Failed to reorder prompts',
    })
    await fetchAll()
  }

  return {
    prompts,
    loading,
    loaded,
    needsAuth,
    loadError,
    fetchAll,
    create,
    update,
    destroy,
    reorder,
  }
})
